import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const GOOGLE_CLIENT_ID = '869515088674-dljs9hemdem4p8bmjingr0kjr5v3ctve.apps.googleusercontent.com';

function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.innerWidth <= 768);
}

function JoinExam() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(false);
  const [test, setTest] = useState(null);
  const [step, setStep] = useState(1); // 1=view test, 2=google sign-in, 3=upload CV, 4=candidate details, 5=preview & start
  const [candidateToken, setCandidateToken] = useState('');
  const [candidate, setCandidate] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [guidelinesAccepted, setGuidelinesAccepted] = useState(false);
  const [currentCTC, setCurrentCTC] = useState('');
  const [totalExperience, setTotalExperience] = useState('');
  const [noticePeriod, setNoticePeriod] = useState('');
  const [expectedCTC, setExpectedCTC] = useState('');
  const googleBtnRef = useRef(null);

  useEffect(() => {
    setIsMobile(isMobileDevice());
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchTest = async () => {
      try {
        const res = await axios.get(`/api/tests/public/${slug}`);
        setTest(res.data.test || res.data);
      } catch (err) {
        setError('Test not found or no longer available.');
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [slug]);

  const handleGoogleResponse = useCallback(async (response) => {
    setError('');
    setActionLoading(true);
    try {
      const res = await axios.post('/api/candidates/register', {
        googleToken: response.credential,
        testSlug: slug
      });
      const token = res.data.token;
      const cand = res.data.candidate;
      setCandidateToken(token);
      setCandidate(cand);
      sessionStorage.setItem('candidateToken', token);
      sessionStorage.setItem('candidateData', JSON.stringify(cand));
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to sign in. Please try again.');
    } finally {
      setActionLoading(false);
    }
  }, [slug]);

  // Initialize Google Sign-In button when step 2 is shown
  useEffect(() => {
    if (step !== 2 || !googleBtnRef.current) return;
    if (!window.google?.accounts?.id) {
      setError('Google Sign-In is still loading. Please wait a moment and try again.');
      return;
    }
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
    });
    window.google.accounts.id.renderButton(googleBtnRef.current, {
      theme: 'outline',
      size: 'large',
      width: '100%',
      text: 'signin_with',
    });
  }, [step, handleGoogleResponse]);

  const handleCvUpload = async (e) => {
    e.preventDefault();
    if (!cvFile) {
      setError('Please select a CV file');
      return;
    }
    setError('');
    setActionLoading(true);

    const formData = new FormData();
    formData.append('cv', cvFile);

    try {
      const res = await axios.post('/api/candidates/upload-cv', formData, {
        headers: {
          Authorization: `Bearer ${candidateToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      const updatedCandidate = res.data.candidate || res.data;
      setCandidate(updatedCandidate);
      sessionStorage.setItem('candidateData', JSON.stringify(updatedCandidate));
      // Pre-fill details if candidate already has them
      if (updatedCandidate.currentCTC) setCurrentCTC(updatedCandidate.currentCTC);
      if (updatedCandidate.totalExperience) setTotalExperience(updatedCandidate.totalExperience);
      if (updatedCandidate.noticePeriod) setNoticePeriod(updatedCandidate.noticePeriod);
      if (updatedCandidate.expectedCTC) setExpectedCTC(updatedCandidate.expectedCTC);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to upload CV. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    if (!currentCTC.trim() || !totalExperience.trim() || !noticePeriod.trim() || !expectedCTC.trim()) {
      setError('All fields are required');
      return;
    }
    setError('');
    setActionLoading(true);
    try {
      const res = await axios.post('/api/candidates/update-details', {
        currentCTC: currentCTC.trim(),
        totalExperience: totalExperience.trim(),
        noticePeriod: noticePeriod.trim(),
        expectedCTC: expectedCTC.trim(),
      }, {
        headers: { Authorization: `Bearer ${candidateToken}` }
      });
      const updatedCandidate = res.data.candidate || res.data;
      setCandidate(updatedCandidate);
      sessionStorage.setItem('candidateData', JSON.stringify(updatedCandidate));
      setStep(5);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save details.');
    } finally {
      setActionLoading(false);
    }
  };

  const startExam = () => {
    navigate(`/exam/${slug}/take`);
  };

  if (isMobile) {
    return (
      <div className="exam-page">
        <div className="exam-container">
          <div className="exam-card error-card">
            <h2>Desktop Required</h2>
            <p>This exam can only be taken on a desktop or laptop computer.</p>
            <p>Please open this link on a desktop browser to proceed.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="exam-page">
        <div className="exam-container">
          <div className="loading-spinner">Loading exam details...</div>
        </div>
      </div>
    );
  }

  if (!test && !loading) {
    return (
      <div className="exam-page">
        <div className="exam-container">
          <div className="exam-card error-card">
            <h2>Test Not Found</h2>
            <p>This exam link is invalid or the test is no longer available.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-page">
      <div className="exam-container">
        {/* Step 1: View Test Info */}
        {step === 1 && (
          <div className="exam-card">
            <div className="exam-card-header">
              <h1>{test.title}</h1>
            </div>
            <div className="exam-card-body">
              {test.jobDescription && (
                <div className="jd-section">
                  <h3>Job Description</h3>
                  <p className="jd-text">{test.jobDescription}</p>
                </div>
              )}
              <div className="exam-info-grid">
                <div className="exam-info-item">
                  <span className="info-label">Duration</span>
                  <span className="info-value">{test.duration} minutes</span>
                </div>
                <div className="exam-info-item">
                  <span className="info-label">Questions</span>
                  <span className="info-value">{test.questionsPerCandidate > 0 ? test.questionsPerCandidate : (test.questions?.length || 0)}</span>
                </div>
                <div className="exam-info-item">
                  <span className="info-label">Passing Score</span>
                  <span className="info-value">{test.passingPercentage}%</span>
                </div>
              </div>
              <button className="btn btn-primary btn-lg btn-full" onClick={() => { setShowGuidelines(true); setGuidelinesAccepted(false); }}>
                Proceed to Sign In
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Google Sign-in */}
        {step === 2 && (
          <div className="exam-card">
            <div className="exam-card-header">
              <h2>Sign In to Take Exam</h2>
              <p className="subtitle">{test.title}</p>
            </div>
            <div className="exam-card-body">
              {error && <div className="error-message">{error}</div>}
              {actionLoading ? (
                <div className="loading-spinner">Verifying your account...</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                  <p>Sign in with your Google account to verify your email</p>
                  <div ref={googleBtnRef}></div>
                </div>
              )}
              <button className="btn btn-link" onClick={() => { setStep(1); setError(''); }}>
                &larr; Back to test info
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Upload CV */}
        {step === 3 && (
          <div className="exam-card">
            <div className="exam-card-header">
              <h2>Upload Your Resume</h2>
              <p className="subtitle">Upload your CV/Resume (PDF format)</p>
            </div>
            <div className="exam-card-body">
              {error && <div className="error-message">{error}</div>}
              <form onSubmit={handleCvUpload}>
                <div className="form-group">
                  <label htmlFor="cvUpload">Select PDF File</label>
                  <div className="file-upload">
                    <input
                      id="cvUpload"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setCvFile(e.target.files[0])}
                      required
                    />
                    {cvFile && <p className="file-name">Selected: {cvFile.name}</p>}
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-lg btn-full" disabled={actionLoading}>
                  {actionLoading ? 'Uploading & Parsing...' : 'Upload CV'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Step 4: Candidate Details */}
        {step === 4 && (
          <div className="exam-card">
            <div className="exam-card-header">
              <h2>Professional Details</h2>
              <p className="subtitle">Please provide the following information</p>
            </div>
            <div className="exam-card-body">
              {error && <div className="error-message">{error}</div>}
              <form onSubmit={handleDetailsSubmit}>
                <div className="form-group">
                  <label htmlFor="currentCTC">Current CTC (in LPA)</label>
                  <input
                    id="currentCTC"
                    type="text"
                    value={currentCTC}
                    onChange={(e) => setCurrentCTC(e.target.value)}
                    placeholder="e.g., 8 LPA"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="totalExperience">Total Experience (in Years)</label>
                  <input
                    id="totalExperience"
                    type="text"
                    value={totalExperience}
                    onChange={(e) => setTotalExperience(e.target.value)}
                    placeholder="e.g., 3 Years"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="noticePeriod">Notice Period (in Months)</label>
                  <input
                    id="noticePeriod"
                    type="text"
                    value={noticePeriod}
                    onChange={(e) => setNoticePeriod(e.target.value)}
                    placeholder="e.g., 2 Months or Immediate"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="expectedCTC">Expected CTC (in LPA)</label>
                  <input
                    id="expectedCTC"
                    type="text"
                    value={expectedCTC}
                    onChange={(e) => setExpectedCTC(e.target.value)}
                    placeholder="e.g., 12 LPA"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-lg btn-full" disabled={actionLoading}>
                  {actionLoading ? 'Saving...' : 'Continue'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Step 5: Profile Preview & Start */}
        {step === 5 && (
          <div className="exam-card">
            <div className="exam-card-header">
              <h2>Ready to Start</h2>
              <p className="subtitle">{test.title}</p>
            </div>
            <div className="exam-card-body">
              {candidate?.profileData && Object.keys(candidate.profileData).length > 0 && (
                <div className="profile-preview">
                  <h3>Your Profile (from CV)</h3>
                  <div className="profile-grid">
                    {(candidate.name || candidate.profileData.name) && (
                      <div className="profile-item">
                        <span className="profile-label">Name</span>
                        <span className="profile-value">{candidate.name || candidate.profileData.name}</span>
                      </div>
                    )}
                    {candidate.email && (
                      <div className="profile-item">
                        <span className="profile-label">Email</span>
                        <span className="profile-value">{candidate.email}</span>
                      </div>
                    )}
                    {(candidate.phone || candidate.profileData.phone) && (
                      <div className="profile-item">
                        <span className="profile-label">Phone</span>
                        <span className="profile-value">{candidate.phone || candidate.profileData.phone}</span>
                      </div>
                    )}
                    {candidate.skills && candidate.skills.length > 0 && (
                      <div className="profile-item profile-item-full">
                        <span className="profile-label">Skills</span>
                        <div className="skills-list">
                          {candidate.skills.map((skill, i) => (
                            <span key={i} className="skill-tag">{skill}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="exam-start-notice">
                <p><strong>You are about to start the exam.</strong></p>
                <p>Timer will begin as soon as you click the button below. Make sure you are ready.</p>
              </div>

              <button className="btn btn-primary btn-lg btn-full" onClick={startExam}>
                Start Exam
              </button>
            </div>
          </div>
        )}
        {/* Guidelines Overlay */}
        {showGuidelines && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            padding: '1rem',
          }}>
            <div style={{
              background: '#fff', borderRadius: '12px', maxWidth: '640px',
              width: '100%', maxHeight: '85vh', overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
              <div style={{
                padding: '1.5rem 2rem', borderBottom: '1px solid #e5e7eb',
                background: '#fef2f2',
              }}>
                <h2 style={{ margin: 0, fontSize: '1.3rem', color: '#991b1b' }}>
                  Assessment Guidelines & Candidate Declaration
                </h2>
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', color: '#666' }}>
                  This assessment is conducted by Frigga Cloud Labs. By proceeding, you agree to comply with the following conditions.
                </p>
              </div>

              <div style={{
                padding: '1.5rem 2rem', overflowY: 'auto', flex: 1,
                fontSize: '0.95rem', lineHeight: '1.6',
              }}>
                <div style={{ marginBottom: '1.25rem' }}>
                  <h3 style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>Assessment Rules</h3>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                    <li>Duration: <strong>{test.duration} minutes</strong> (strict)</li>
                    <li>Single attempt only — no pauses or retries</li>
                    <li>Timer starts immediately upon beginning</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                  <h3 style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>System Restrictions</h3>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                    <li>No tab switching or leaving the exam screen</li>
                    <li>No copy-paste, right-click, or developer tools</li>
                    <li>Any attempt to bypass restrictions will be automatically logged</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                  <h3 style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>Prohibited Activities</h3>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                    <li>Use of internet resources or documentation</li>
                    <li>Use of AI tools (ChatGPT, Copilot, etc.)</li>
                    <li>Assistance from any person or external device</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                  <h3 style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>Monitoring</h3>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                    <li>The assessment is AI-proctored</li>
                    <li>Activities such as tab switching or abnormal behavior are tracked and flagged</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                  <h3 style={{ fontSize: '1.05rem', marginBottom: '0.5rem', color: '#991b1b' }}>Enforcement</h3>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                    <li>Immediate termination upon violation</li>
                    <li>Permanent disqualification from Frigga Cloud Labs</li>
                    <li>Ineligibility for all future opportunities</li>
                    <li>Decisions are final and non-appealable</li>
                  </ul>
                </div>

                <div style={{
                  marginBottom: '1.25rem', padding: '1rem',
                  background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0',
                }}>
                  <h3 style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>Candidate Declaration</h3>
                  <p style={{ margin: 0 }}>By starting the test, you confirm that:</p>
                  <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                    <li>You will complete it independently and honestly</li>
                    <li>You accept all rules and monitoring policies</li>
                  </ul>
                </div>

                <div style={{
                  padding: '0.75rem 1rem', background: '#fffbeb',
                  borderRadius: '8px', border: '1px solid #fde68a',
                  fontSize: '0.9rem', color: '#92400e',
                }}>
                  <strong>Integrity Notice:</strong> Frigga Cloud Labs follows a zero-tolerance policy toward malpractice. Only genuine skills will be considered.
                </div>
              </div>

              <div style={{
                padding: '1.25rem 2rem', borderTop: '1px solid #e5e7eb',
                background: '#f9fafb',
              }}>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  cursor: 'pointer', marginBottom: '1rem', fontSize: '0.95rem',
                }}>
                  <input
                    type="checkbox"
                    checked={guidelinesAccepted}
                    onChange={(e) => setGuidelinesAccepted(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  I have read and accept all the guidelines, rules, and monitoring policies.
                </label>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    className="btn btn-link"
                    onClick={() => { setShowGuidelines(false); setGuidelinesAccepted(false); }}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-lg"
                    disabled={!guidelinesAccepted}
                    onClick={() => { setShowGuidelines(false); setStep(2); }}
                    style={{ flex: 2, opacity: guidelinesAccepted ? 1 : 0.5 }}
                  >
                    Proceed to Sign In
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default JoinExam;
